import { useContext, useEffect } from "react";
import { useImmer } from "use-immer";
import { getAdminUsersList, deleteUserById, deleteStoresFromUser } from "..";
import qs from "qs";
import { useDebounce, useDebouncedCallback } from "use-debounce";
import { errorMessage, successMessage, getStoreId } from "../../../utils";
import { TableStateContext } from "../../../store";

export const useStoreAdminUsers = ({ isList }) => {
  const [state, setState] = useImmer({
    isLoading: false,
    usersList: [],
    deleteModalOpen: false,
    selectedUser: null,
    searchText: null,
    status: null,
    isSearching: false,
  });

  const storeId = getStoreId();
  const PAGE_NAME = "storeAdmin";
  const {
    filterState: {
      params: { storeAdmin },
    },
    initialState,
    filterStateDispatch,
  } = useContext(TableStateContext);

  const debounced = useDebouncedCallback((value) => {
    const { status, searchText } = state;
    filterStateDispatch({
      type: "SET_DATA",
      page: PAGE_NAME,
      data: {
        filterApplied: { status: state.status, searchText: state.searchText },
      },
    });
    handleUsersList(searchText, status);
  }, 1000);

  useEffect(() => {
    if (isList) {
      const { searchText, status } = storeAdmin.filterApplied;
      setState((draft) => {
        draft.status = storeAdmin.filterApplied.status;
        draft.searchText = storeAdmin.filterApplied.searchText;
      });
      handleUsersList(searchText, status);
    }
  }, [isList]);

  //Admin User List
  const handleUsersList = async (searchText = "", status = null) => {
    try {
      setState((draft) => {
        draft.isLoading = true;
      });
      let params = {};
      if (searchText) {
        params = { ...params, search: searchText };
      }
      if (status !== null) {
        params = { ...params, status: status };
      }
      const query = {
        params,
        paramsSerializer: (params) => qs.stringify(params, { encode: true }),
      };
      const res = await getAdminUsersList(query);
      if (res.success) {
        setState((draft) => {
          draft.usersList = res.data;
          draft.isLoading = false;
          draft.isSearching = false;
        });
      }
    } catch (error) {
      setState((draft) => {
        draft.isLoading = false;
        draft.isSearching = false;
      });
    }
  };

  const handleSearch = (v) => {
    setState((draft) => {
      draft.searchText = v;
    });
    debounced(v);
  };

  const onSeachClear = () => {
    setState((draft) => {
      draft.searchText = "";
    });
    debounced("");
  };

  const handleModal = (id = null) => {
    setState((draft) => {
      draft.deleteModalOpen = !draft.deleteModalOpen;
      draft.selectedUser = id;
    });
  };

  const handleDeleteUser = async () => {
    try {
      const res = await deleteStoresFromUser(state.selectedUser, storeId);
      if (res.success) {
        successMessage("User removed from this store successfully");
        setState((draft) => {
          draft.usersList = draft.usersList.filter(
            (item) => item.id !== state.selectedUser
          );
        });
        handleModal();
      }
    } catch (err) {
      errorMessage(err.response.data.message);
    }
  };

  const handleStatusFilter = (e) => {
    setState((draft) => {
      draft.status = e.target.value;
    });
    const { searchText } = state;
    handleUsersList(searchText, e.target.value);

    filterStateDispatch({
      type: "SET_DATA",
      page: PAGE_NAME,
      data: {
        filterApplied: { status: e.target.value, searchText: searchText },
      },
    });
  };

  const clearFilters = () => {
    if (state.status !== null || state.searchText !== null) {
      setState((draft) => {
        draft.isSearching = true;
        draft.status = null;
        draft.searchText = null;
      });
      handleUsersList();
      const initialValue = {
        status: null,
        searchText: null,
      };
      filterStateDispatch({
        type: "CLEAR_FILTERS",
        page: PAGE_NAME,
        value: initialValue,
      });
    }
  };

  return {
    state,
    handleModal,
    handleDeleteUser,
    handleStatusFilter,
    handleSearch,
    onSeachClear,
    clearFilters,
  };
};
